// Carrito del paso Producto: las líneas de la sesión en el vocabulario de la pantalla, su total y
// qué se puede hacer con ellas. Puro, sin React.
import { SERVICE_MAX_QTY, isService } from './catalogAvailability';
import { LOST_COPY } from './integrationErrors';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { PRODUCT_CATEGORY_LABEL } from '@/shared/lib/format/productCategory';
import type {
    CatalogItem,
    LineStatus,
    ProductCategory,
    ReleaseProductTarget,
    ReservedLine,
    WizardSession,
} from './types';

/** Tope de piezas de una venta; el silo responde `SALE_TOO_LARGE` al pasarlo. */
export const MAX_SALE_PIECES = 99;

export const CATEGORY_LABEL: Record<ProductCategory, string> = PRODUCT_CATEGORY_LABEL;

export type CategoryFilter = ProductCategory | 'ALL';

/** Pestañas de categoría del catálogo, en el orden del mostrador. */
export const CATEGORY_FILTERS: { value: CategoryFilter; label: string }[] = [
    { value: 'ALL', label: 'Todo' },
    { value: 'PHONE', label: 'Teléfonos' },
    { value: 'BUNDLE', label: 'Kits' },
    { value: 'SIM', label: 'SIM' },
    { value: 'MODEM', label: 'Módems' },
    { value: 'ACCESSORY', label: 'Accesorios' },
];

/** Una fila del carrito. `lineId` en `null` = equipo de una sesión anterior a las líneas. */
export interface CartLine {
    key: string;
    lineId: string | null;
    imei: string | null;
    sku: string;
    name: string;
    detail: string;
    /** Nombre de alta del catálogo, si lo trae; distingue dos SKUs de la misma marca y modelo. */
    description?: string;
    category: ProductCategory;
    qty: number;
    /** Precio por pieza fijado al apartar; `null` mientras la línea está PENDING. */
    unitPriceCents: number | null;
    totalCents: number | null;
    status: LineStatus;
    /** Solo en LOST: cómo dejó Zentral la pieza. */
    lostReason?: ReservedLine['lostReason'];
    expiresAt: string | undefined;
    /** Regalo de una promo: va en $0 y no se cobra de contado. */
    gift?: boolean;
    /** Promo que puso el descuento o el regalo; su nombre lo resuelve Promociones. */
    promotion?: LinePromotionRef;
    /** Todas las promos de la línea, una etiqueta por promo; ausente si no trae. */
    promotions?: LinePromotionRef[];
}

/** Promos de la línea con descuento; en líneas viejas, la de `promotionId`. */
export function promotionRefsOf(line: {
    promotionId?: string;
    promotionVersion?: number;
    promotionDiscounts?: { promotionId: string; promotionVersion: number; discountCents: number }[];
}): LinePromotionRef[] {
    if (line.promotionDiscounts && line.promotionDiscounts.length > 0) {
        return line.promotionDiscounts
            .filter((d) => d.discountCents > 0)
            .map((d) => ({ promotionId: d.promotionId, promotionVersion: d.promotionVersion }));
    }
    if (line.promotionId === undefined) return [];
    return [
        {
            promotionId: line.promotionId,
            ...(line.promotionVersion !== undefined && { promotionVersion: line.promotionVersion }),
        },
    ];
}

export interface LinePromotionRef {
    promotionId: string;
    promotionVersion?: number;
}

/** Sin categoría (datos anteriores a las líneas) el producto es un teléfono. */
export function categoryOf(item: Pick<CatalogItem, 'category'>): ProductCategory {
    return item.category ?? 'PHONE';
}

export function filterByCategory(items: CatalogItem[], category: CategoryFilter): CatalogItem[] {
    return category === 'ALL' ? items : items.filter((i) => categoryOf(i) === category);
}

/** Etiqueta del tipo de SKU; un servicio se nombra así aunque su categoría sea accesorio. */
export function itemLabelOf(item: Pick<CatalogItem, 'category' | 'itemType'>): string {
    return isService(item) ? 'Servicio' : CATEGORY_LABEL[categoryOf(item)];
}

/** Piezas que se pueden agregar: su disponible sin pasar del tope; un servicio, su tope propio. */
export function maxQtyFor(item: Pick<CatalogItem, 'itemType' | 'stock'>): number {
    if (isService(item)) return SERVICE_MAX_QTY;
    return Math.max(0, Math.min(item.stock ?? 0, MAX_SALE_PIECES));
}

function detailOf(category: ProductCategory, qty: number, service = false): string {
    if (category === 'BUNDLE') return 'Kit';
    const parts = [service ? 'Servicio' : CATEGORY_LABEL[category]];
    if (qty > 1) parts.push(`×${String(qty)}`);
    return parts.join(' · ');
}

/** SKUs de servicio del catálogo; la línea apartada no dice si es servicio. */
export function serviceSkusOf(items: Pick<CatalogItem, 'sku' | 'itemType'>[]): Set<string> {
    return new Set(items.filter(isService).map((i) => i.sku));
}

/** Nombre visible por SKU del catálogo; la línea apartada no lo trae. */
export function descriptionsBySku(
    items: Pick<CatalogItem, 'sku' | 'description'>[],
): Map<string, string> {
    return new Map(items.flatMap((i) => (i.description ? [[i.sku, i.description] as const] : [])));
}

function fromLine(
    line: ReservedLine,
    serviceSkus: ReadonlySet<string>,
    descriptions: ReadonlyMap<string, string>,
): CartLine {
    const gift = line.role === 'GIFT';
    // Un regalo que el catálogo no describe es un accesorio, no el teléfono que se asume por defecto.
    const category = line.product?.category ?? (gift ? 'ACCESSORY' : 'PHONE');
    const unit = line.basePriceCents;
    const description = descriptions.get(line.sku);
    return {
        key: line.lineId,
        lineId: line.lineId,
        imei: null,
        sku: line.sku,
        name: catalogDisplayName({ sku: line.sku, ...line.product }, descriptions),
        detail: detailOf(
            line.components.length > 0 ? 'BUNDLE' : category,
            line.qty,
            serviceSkus.has(line.sku),
        ),
        ...(description !== undefined && { description }),
        category,
        qty: line.qty,
        unitPriceCents: unit,
        totalCents: unit === null ? null : unit * line.qty,
        status: line.status,
        ...(line.lostReason !== undefined && { lostReason: line.lostReason }),
        expiresAt: line.expiresAt,
        ...(gift && { gift }),
        ...(promotionRefsOf(line).length > 0 && { promotions: promotionRefsOf(line) }),
        ...(line.promotionId !== undefined && {
            promotion: {
                promotionId: line.promotionId,
                ...(line.promotionVersion !== undefined && {
                    promotionVersion: line.promotionVersion,
                }),
            },
        }),
    };
}

/** Líneas de la venta; la sesión anterior a las líneas aporta su equipo apartado como una fila. */
export function cartLinesOf(
    session: WizardSession | undefined,
    serviceSkus: ReadonlySet<string> = new Set(),
    descriptions: ReadonlyMap<string, string> = new Map(),
): CartLine[] {
    if (!session) return [];
    if (session.reservedLines) {
        return session.reservedLines.map((l) => fromLine(l, serviceSkus, descriptions));
    }
    const p = session.product;
    if (!p) return [];
    return [
        {
            key: `legacy-${p.sku}`,
            lineId: null,
            imei: p.imei ?? null,
            sku: p.sku,
            name: catalogDisplayName(p, descriptions),
            detail: detailOf('PHONE', 1),
            category: 'PHONE',
            qty: 1,
            unitPriceCents: p.cashPriceCents,
            totalCents: p.cashPriceCents,
            status: 'RESERVED',
            expiresAt: session.reservationExpiresAt,
        },
    ];
}

/** Total de la venta: las líneas que se están liberando o que Zentral se llevó ya no se cobran. */
export function cartTotalCents(lines: CartLine[]): number {
    return lines
        .filter((l) => l.status !== 'RELEASING' && l.status !== 'LOST')
        .reduce((sum, l) => sum + (l.totalCents ?? 0), 0);
}

/** Hay algo apartado y nada a medio apartar ni a medio liberar (el silo no cobraría). */
export function canContinue(lines: CartLine[]): boolean {
    return (
        lines.some((l) => l.status === 'RESERVED') &&
        !lines.some((l) => l.status === 'PENDING') &&
        continueBlocker(lines) === undefined
    );
}

/** Por qué no se puede avanzar al cobro con estas líneas; `undefined` si nada lo impide. */
export function continueBlocker(lines: CartLine[]): string | undefined {
    if (lines.some((l) => l.status === 'RELEASING')) {
        return 'Reintenta quitar los productos que no se liberaron para continuar.';
    }
    if (lines.some((l) => l.status === 'LOST')) return LOST_COPY.blocker;
    return undefined;
}

/** Qué mandar en el DELETE; `null` si la fila no se puede soltar (sesión vieja sin IMEI). */
export function releaseTargetOf(line: CartLine): ReleaseProductTarget | null {
    if (line.lineId !== null) return { lineId: line.lineId };
    return line.imei !== null ? { imei: line.imei } : null;
}
